import * as React from "react";
import { useEffect, useState } from "react";
import styled from "@mui/material/styles/styled";
import { PatientCardCommon } from "fe-web-components/patientCard/patientCardCommon";
import {
  ungroupPatientCase,
  useGetShareId,
} from "../diagnosticAgent/useStoredPatientCase";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { Button, Card } from "@mui/material";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";
import { useClinicState } from "../../clinic/clinicStateContext";
import QuestionAnswerRoundedIcon from "@mui/icons-material/QuestionAnswerRounded";
import { CLINIC_CHAT_LINK } from "./clinicSettings/clinicUrls";
import { useDaoContext } from "fe-web-components/diseaseview/DaoContext";
import { PatientCase } from "../../context/diagnosticAgent";
import {
  ClinicPatientCardTitle,
  CommonPatientCardTitleRow,
} from "fe-web-components/patientCard/commonPatientCardTitleRow";
import { ClinicLoading } from "./uiTemplates/clinicLoading";

const themeSettings = require("./clinicSettings/defaultSettings/theme.json");
//region [[ Styles ]]

const ClinicPatientCardView = styled(Card)({
  minHeight: "100vh",
  backgroundColor: KahunColorMap.LightGrayBackground,
});

const ClinicPatientCardHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "36px 0",
  backgroundColor: "white",
  position: "relative",
  boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.08)",
  [theme.breakpoints.down("xs")]: {
    padding: "0",
    flexWrap: "wrap",
    "& > *": {
      margin: "15px 0",
    },
  },
}));

const TopButtons = styled("div")(({ theme }) => ({
  display: "flex",
  flexFlow: "row",
  "& > *": {
    margin: "10px",
  },
  [theme.breakpoints.down("xs")]: {
    flexFlow: "column",
  },
}));

const MobilePatientCardTitle = styled(ClinicPatientCardTitle)(({ theme }) => ({
  display: "none",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  marginTop: "20px",
  marginBottom: "40px",
  [theme.breakpoints.down("xs")]: {
    display: "flex",
  },
}));

const LearnMoreButton = styled(Button)(({ theme }) => ({
  background: "#5A52C3",
  color: "white",
  "&:hover": {
    background: "#5A52C3",
    color: "white",
    opacity: 0.8,
  },
  "& span": {
    textTransform: "none",
    fontSize: "14px",
    fontWeight: 700,
  },
  margin: "10px",
  borderRadius: "8px",
  [theme.breakpoints.down("xs")]: {
    "& span": {
      fontSize: "12px",
    },
  },
}));

const PatientData = styled(PatientCardCommon)({
  "& .patient-info-wrapperr": {
    padding: 0,
    margin: 0,
    marginTop: "25px",
  },
});

const ConversationIcon = styled(QuestionAnswerRoundedIcon)({
  margin: "0 5px",
});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface ClinicPatientCardProps {}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const ClinicPatientCard = () => {
  const shareId = useGetShareId();
  const dao = useDaoContext();
  const [patientCase, setPatientCase] = useState<PatientCase>(null); //for the structure toggle

  const [{ loading: clinicLoading, clinicData }] = useClinicState();

  useEffect(() => {
    evt({
      category: CATEGORY.CLINIC_MANAGEMENT,
      action: ACTION.CLINIC_PATIENT_PROVIDER_LINK,
      label: `Patient CaseId:${shareId}`,
    });
  }, [shareId]);

  useEffect(() => {
    dao.restorePatientCase(shareId).then((res) => {
      setPatientCase(ungroupPatientCase(res.findings));
    });
  }, [dao, shareId]);

  if (clinicLoading || !clinicData)
    return <ClinicLoading title={"Loading patient Assessment"} />;

  const goToKahunAboutUs = () => {
    window.open("https://kahun.com/about", "_blank");
  };

  const goToConversation = () => {
    window.open(
      `${CLINIC_CHAT_LINK}${clinicData.id}/?patientCaseId=${shareId}`,
    );
  };

  return (
    <ClinicPatientCardView className={"clinic-patient-card"}>
      <ClinicPatientCardHeader className={"clinic-patient-card-header"}>
        <CommonPatientCardTitleRow>
          <TopButtons className={"top-buttons"}>
            <LearnMoreButton onClick={goToConversation}>
              <ConversationIcon />
              Open this conversation
            </LearnMoreButton>
            <LearnMoreButton onClick={goToKahunAboutUs}>
              Learn more about us
            </LearnMoreButton>
          </TopButtons>
        </CommonPatientCardTitleRow>
        <MobilePatientCardTitle>Patient Assessment</MobilePatientCardTitle>
      </ClinicPatientCardHeader>
      <PatientData
        caseId={shareId}
        clinicId={clinicData.id}
        supportedSections={
          [] /* ---> TODO: read supported sections from clinic settings*/
        }
        topActions={[]}
        patientCase={patientCase}
        themeSettings={themeSettings}
      />
    </ClinicPatientCardView>
  );
};
